import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQueries } from 'react-query';
import { postComplianceProgram } from 'services/complice-program';
import { getFrameworks, getControlsGrouped } from 'services/gap-analysis';
import { Main as Container } from 'Components/Main';
import React, { useEffect, useState } from 'react';
import FrameworkSelector from 'Components/FrameworkSelector';
import DomainControlsList from 'Components/DomainControlsList';
import { useNavigate } from 'react-router-dom';
import { PageHelper } from 'Components/PageHelper';
import { PageHeader, Button } from 'Components/ui';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const GREEN = 'var(--vo-success)';
const BG = 'var(--vo-mist)';
const BORDER_IDLE = 'var(--vo-steel)';
const DIVIDER = 'var(--vo-cloud)';

/* ── Stepper card ── */
const StepperCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 20px;
`;
const StepperRow = styled.div`
  display: flex;
  align-items: flex-start;
`;
const StepCircleWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
`;
const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active, $done }) => $done ? GREEN : $active ? BLUE : DIVIDER};
  color: ${({ $active, $done }) => ($active || $done) ? 'white' : BORDER_IDLE};
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 700;
`;
const StepLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: ${({ $active, $done }) => ($active || $done) ? 600 : 400};
  color: ${({ $active, $done }) => $done ? GREEN : $active ? BLUE : BORDER_IDLE};
  white-space: nowrap;
  text-align: center;
  max-width: 72px;
`;
const StepLine = styled.div`
  flex: 1;
  height: 2px;
  margin: 0 4px;
  margin-top: 15px;
  background: ${({ $done }) => $done ? GREEN : DIVIDER};
`;

/* ── Form card ── */
const FormCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  ${({ $wide }) => !$wide && 'max-width: 600px;'}
  margin-bottom: 20px;
`;
const CardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 ${({ $noMb }) => $noMb ? '4px' : '20px'};
`;
const CardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0 0 20px;
`;
const ErrorMsg = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-danger);
`;

/* ── Summary ── */
const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
`;
const SummaryLabel = styled.div`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .06em;
  padding: 12px 0;
  border-bottom: ${({ $last }) => $last ? 'none' : '1px solid var(--vo-mist)'};
`;
const SummaryValue = styled.div`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${INK};
  font-weight: 500;
  padding: 12px 0;
  border-bottom: ${({ $last }) => $last ? 'none' : '1px solid var(--vo-mist)'};
`;
const FwPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const FwPill = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${BLUE};
  background: var(--vo-capri-50);
  padding: 3px 10px;
  border-radius: 20px;
`;

/* ── Nav row ── */
const NavRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 24px;
`;
const BackBtn = styled.button`
  height: 44px;
  background: white;
  color: ${MUTED};
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  &:hover { background: ${BG}; }
`;

const STEP_KEYS = ['frameworks', 'controls', 'summary'];

export const ComplianceProgramForm = () => {
  const { t } = useTranslation(['company']);
  const selectedTenant = useSelector(useAuthTenant);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);
  const [controls, setControls] = useState([]);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useForm({
    defaultValues: {
      company_id: selectedTenant.slug || '',
      type: 'company',
      frameworks: [],
    },
  });

  useEffect(() => {
    if (selectedTenant) setValue('company_id', selectedTenant.slug);
  }, [selectedTenant, setValue]);

  const results = useQueries([
    { queryKey: ['frameworks'], queryFn: getFrameworks },
  ]);

  const frameworksData = results[0].data?.data || [];

  useEffect(() => {
    const fetchControlsGrouped = async ids => {
      if (ids.length === 0) { setControls([]); return; }
      try {
        const result = await getControlsGrouped(ids);
        if (result.status === 200) setControls(result.data);
      } catch (_) { /* ignore */ }
    };
    fetchControlsGrouped(selectedFrameworks.map(fw => fw._id));
  }, [selectedFrameworks]);

  useEffect(() => {
    if (step === 0) {
      register('frameworks', {
        validate: value => (Array.isArray(value) && value.length > 0) || t('company:complianceProgram.form.frameworksRequired'),
      });
      if (!watch('frameworks') || watch('frameworks').length === 0) {
        setValue('frameworks', selectedFrameworks.map(fw => fw._id));
      }
    }
  }, [register, step, setValue, watch, selectedFrameworks, t]);

  const handleToggle = framework => {
    setSelectedFrameworks(prev => {
      const isSelected = prev.some(item => item._id === framework._id);
      const updated = isSelected
        ? prev.filter(item => item._id !== framework._id)
        : [...prev, framework];
      setValue('frameworks', updated.map(fw => fw._id));
      trigger('frameworks');
      return updated;
    });
  };

  const handleNext = async () => {
    let isValid = await trigger();
    if (step === 0) {
      const fwValid = await trigger('frameworks');
      isValid = isValid && fwValid;
    }
    if (isValid) setStep(prev => prev + 1);
  };

  const submitComplianceProgram = handleSubmit(async data => {
    try {
      const payload = { ...data, framework_ids: selectedFrameworks.map(fw => fw._id) };
      delete payload.frameworks;
      const response = await postComplianceProgram(payload);
      if (response.status === 201 || response.status === 200) {
        navigate('/compliance-program');
      }
    } catch (_) {
      alert(t('company:complianceProgram.form.errorMessage'));
    }
  });

  const stepLabels = STEP_KEYS.map(key => t(`company:complianceProgram.form.steps.${key}`));
  const totalControls = controls.reduce((acc, domain) => acc + (domain.count ?? domain.children?.length ?? 0), 0);
  const totalDomains = controls.length;

  const tourSteps = [
    { target: 'body', content: t('company:complianceProgram.form.tour.step0'), placement: 'center', disableBeacon: true },
    { target: '[data-tour="page-title"]', content: t('company:complianceProgram.form.tour.step1'), placement: 'bottom' },
    { target: '[data-tour="stepper"]', content: t('company:complianceProgram.form.tour.step2'), placement: 'bottom' },
    { target: '[data-tour="framework-selector"]', content: t('company:complianceProgram.form.tour.step3'), placement: 'top' },
    { target: '[data-tour="next-button"]', content: t('company:complianceProgram.form.tour.step4'), placement: 'top' },
    { target: 'body', content: t('company:complianceProgram.form.tour.step5'), placement: 'center' },
    { target: 'body', content: t('company:complianceProgram.form.tour.step6'), placement: 'center' },
    { target: 'body', content: t('company:complianceProgram.form.tour.step7'), placement: 'center' },
    { target: 'body', content: t('company:complianceProgram.form.tour.step8'), placement: 'center' },
  ];

  return (
    <Container>
      <PageHelper steps={tourSteps} tourKey="compliance-program-form-tour" autoStart={true} showButton={true} />

      <PageHeader
        data-tour="page-title"
        title={t('company:complianceProgram.form.title')}
        segments={[
          { label: t('common:breadcrumbs.manage') },
          { label: t('company:complianceProgram.form.title') },
        ]}
      />

      {/* ── Stepper ── */}
      <StepperCard data-tour="stepper">
        <StepperRow>
          {STEP_KEYS.map((key, i) => (
            <React.Fragment key={key}>
              {i > 0 && <StepLine $done={step > i - 1} />}
              <StepCircleWrap>
                <StepCircle $active={step === i} $done={step > i}>
                  {step > i ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : i + 1}
                </StepCircle>
                <StepLabel $active={step === i} $done={step > i}>{stepLabels[i]}</StepLabel>
              </StepCircleWrap>
            </React.Fragment>
          ))}
        </StepperRow>
      </StepperCard>

      {/* ── Step content ── */}
      <FormCard $wide={step === 0 || step === 1}>
        {step === 0 && (
          <>
            <CardTitle $noMb>{t('company:complianceProgram.form.cardTitle.step2')}</CardTitle>
            <CardSubtitle>{t('company:complianceProgram.form.step2Sub')}</CardSubtitle>
            <div data-tour="framework-selector">
              <FrameworkSelector
                frameworks={frameworksData}
                selectedFrameworks={selectedFrameworks}
                handleToggle={handleToggle}
              />
            </div>
            {errors.frameworks && <ErrorMsg>{errors.frameworks.message}</ErrorMsg>}
          </>
        )}

        {step === 1 && (
          <>
            <CardTitle $noMb>{t('company:complianceProgram.form.cardTitle.step3')}</CardTitle>
            <CardSubtitle>{t('company:complianceProgram.form.step3Sub')}</CardSubtitle>
            <DomainControlsList data={controls} />
          </>
        )}

        {step === 2 && (
          <>
            <CardTitle>{t('company:complianceProgram.form.cardTitle.step4')}</CardTitle>
            <SummaryGrid>
              <SummaryLabel>{t('company:complianceProgram.form.summary.frameworksLabel')}</SummaryLabel>
              <SummaryValue>
                <FwPills>
                  {selectedFrameworks.map(fw => (
                    <FwPill key={fw._id}>{fw.sourceIdentifier}</FwPill>
                  ))}
                </FwPills>
              </SummaryValue>
              <SummaryLabel $last>{t('company:complianceProgram.form.summary.controlsLabel')}</SummaryLabel>
              <SummaryValue $last>
                {t('company:complianceProgram.form.summary.controlsValue', { count: totalControls, domains: totalDomains })}
              </SummaryValue>
            </SummaryGrid>
          </>
        )}
      </FormCard>

      {/* ── Navigation ── */}
      <NavRow data-tour="next-button">
        {step > 0 && (
          <BackBtn type="button" onClick={() => setStep(prev => prev - 1)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {t('company:complianceProgram.form.backButton')}
          </BackBtn>
        )}
        {step < 2 && (
          <Button
            variant="primary"
            type="button"
            onClick={handleNext}
            endIcon={
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            }
            sx={{ height: '44px', padding: '0 24px' }}
          >
            {t('company:complianceProgram.form.nextButton')}
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="primary"
            type="button"
            onClick={submitComplianceProgram}
            startIcon={
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            }
            sx={{ height: '44px', padding: '0 24px' }}
          >
            {t('company:complianceProgram.form.saveButton')}
          </Button>
        )}
      </NavRow>
    </Container>
  );
};
